/**
 * Network utilities for error detection and retry logic
 */

import { logNetworkError } from './errorLogger';

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
};

/**
 * Check if an error is a network error that should be retried
 */
export function isRetryableNetworkError(error: any): boolean {
  // No response received (network error)
  if (error.request && !error.response) {
    return true;
  }

  // Server errors (5xx) are generally retryable
  if (error.response && error.response.status >= 500) {
    return true;
  }

  // Rate limiting (429) is retryable after delay
  if (error.response && error.response.status === 429) {
    return true;
  }

  // Timeout errors are retryable
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return true;
  }

  return false;
}

/**
 * Check if the user is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Wait for the user to come back online
 */
export function waitForOnline(): Promise<void> {
  return new Promise((resolve) => {
    if (isOnline()) {
      resolve();
      return;
    }

    const handleOnline = () => {
      window.removeEventListener('online', handleOnline);
      resolve();
    };

    window.addEventListener('online', handleOnline);
  });
}

/**
 * Calculate delay for exponential backoff
 */
export function calculateBackoffDelay(attempt: number, config: RetryConfig): number {
  const delay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt);
  return Math.min(delay, config.maxDelay);
}

/**
 * Sleep for a specified number of milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  context: string = 'unknown'
): Promise<T> {
  let lastError: Error = new Error('Unknown error');

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      // Wait for online status before attempting
      await waitForOnline();

      const result = await fn();

      // Log successful retry if this wasn't the first attempt
      if (attempt > 0) {
        console.info(`Retry successful after ${attempt} attempts for ${context}`);
      }

      return result;
    } catch (error) {
      lastError = error as Error;

      // Log the error
      logNetworkError(lastError, context, attempt);

      // Don't retry if it's not a retryable error
      if (!isRetryableNetworkError(error)) {
        throw error;
      }

      // Don't retry if we've reached max attempts
      if (attempt === config.maxRetries) {
        break;
      }

      // Calculate delay and wait
      const delay = calculateBackoffDelay(attempt, config);
      console.warn(
        `Retry attempt ${attempt + 1}/${config.maxRetries} for ${context} in ${delay}ms`
      );
      await sleep(delay);
    }
  }

  throw lastError;
}

/**
 * Create a retry wrapper for API functions
 */
export function withRetry<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  context?: string
): T {
  return ((...args: Parameters<T>) => {
    const contextName = context || fn.name || 'api_call';
    return retryWithBackoff(() => fn(...args), config, contextName);
  }) as T;
}

/**
 * Network status monitor
 */
export class NetworkMonitor {
  private listeners: Array<(online: boolean) => void> = [];
  private _isOnline: boolean = navigator.onLine;

  constructor() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  private handleOnline = () => {
    this._isOnline = true;
    this.notifyListeners(true);
  };

  private handleOffline = () => {
    this._isOnline = false;
    this.notifyListeners(false);
  };

  private notifyListeners(online: boolean) {
    this.listeners.forEach((listener) => {
      try {
        listener(online);
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
  }

  get isOnline(): boolean {
    return this._isOnline;
  }

  addListener(listener: (online: boolean) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  destroy() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    this.listeners = [];
  }
}

// Export singleton instance
export const networkMonitor = new NetworkMonitor();
